import React, { useCallback, useEffect, useMemo, useState } from "react";
import MUIDataTable from "mui-datatables";
import { Button, IconButton, Dialog, DialogActions, DialogContent, Box, Chip, Switch } from "@mui/material";
import { Edit, DeleteOutline, Close, ContentCopy } from "@mui/icons-material";
import { Helmet } from "react-helmet";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Axios from "../axios";
import { useAuth } from "../auth/authContext";

// Dates are stored as UTC midnight and represent a calendar day, not an instant —
// always read them back via the UTC getters/toISOString so a viewer whose local
// timezone is behind UTC doesn't see the previous day.
const toDateOnly = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString().split("T")[0];
};

const formatDisplayDate = (value) => {
  const dateOnly = toDateOnly(value);
  if (!dateOnly) return "-";
  const [y, m, d] = dateOnly.split("-").map(Number);
  return `${m}/${d}/${y}`;
};

const calculateNights = (startDate, endDate) => {
  if (!startDate || !endDate) return "";
  const start = new Date(startDate);
  const end = new Date(endDate);
  const nights = Math.round((end - start) / (1000 * 60 * 60 * 24));
  return nights > 0 ? nights : "";
};

const getExpiryStatus = (diffDays) => {
  if (diffDays === null || diffDays === undefined) return { label: "No Date", color: "default" };
  if (diffDays < 0) return { label: "Expired", color: "error" };
  if (diffDays === 0) return { label: "Expires Today", color: "error" };
  if (diffDays <= 7) return { label: `${diffDays} day${diffDays > 1 ? "s" : ""} left`, color: "warning" };
  return { label: `${diffDays} days left`, color: "success" };
};

const computeDaysUntilExpiry = (startDate) => {
  const dateOnly = toDateOnly(startDate);
  if (!dateOnly) return null;
  const [y, m, d] = dateOnly.split("-").map(Number);
  const startUTC = Date.UTC(y, m - 1, d);
  const today = new Date();
  const todayUTC = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.ceil((startUTC - todayUTC) / (1000 * 60 * 60 * 24));
};

const Deals = () => {
  const { navData } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [write, setWrite] = useState(false);
  const [deleteState, setDeleteState] = useState({ status: false, id: "" });
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [count, setCount] = useState(0);
  const [search, setSearch] = useState("");
  const [sortOrder, setSortOrder] = useState({ name: "start_date", direction: "asc" });
  const BACKEND_URL = process.env.REACT_APP_BACKEND;

  useEffect(() => {
    const currentPath = location.pathname;
    const matchingNavItem = navData.find((item) => item.link === currentPath);
    setWrite(Boolean(matchingNavItem?.write));
  }, [location.pathname, navData]);

  const showError = (err) => {
    toast.error(err?.response?.data?.message || err?.response?.data?.error || "Something went wrong");
  };

  const fetchDeals = useCallback((pageIndex, searchText, sort) => {
    Axios.get(`${BACKEND_URL}package`, {
      params: {
        page: pageIndex + 1,
        limit: 10,
        search: searchText,
        sortField: sort?.name || "",
        sortOrder: sort?.direction || "",
      },
    })
      .then((res) => {
        setRows(res?.data?.data?.docs || []);
        setCount(res?.data?.data?.totalDocs || 0);
      })
      .catch(showError);
  }, [BACKEND_URL]);

  const dealsWithExpiry = useMemo(() => rows.map((d) => ({
    ...d,
    daysUntilExpiry: computeDaysUntilExpiry(d.start_date),
  })), [rows]);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchDeals(page, search, sortOrder);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchDeals]);

  const deleteDeal = () => {
    setLoading(true);
    Axios.delete(`${BACKEND_URL}package/${deleteState.id}`)
      .then((res) => {
        toast.success(res?.data?.message || "Deleted successfully");
        setDeleteState({ status: false, id: "" });
        fetchDeals(page, search, sortOrder);
      })
      .catch(showError)
      .finally(() => setLoading(false));
  };

  const cloneDeal = (id) => {
    setLoading(true);
    Axios.post(`${BACKEND_URL}package/${id}/clone`)
      .then((res) => {
        toast.success(res?.data?.message || "Cloned successfully");
        fetchDeals(page, search, sortOrder);
        const newId = res?.data?.data?._id;
        if (newId) navigate(`/deals/edit/${newId}`, { state: { fromClone: true } });
      })
      .catch(showError)
      .finally(() => setLoading(false));
  };

  const toggleStatus = (id, current) => {
    setLoading(true);
    const req = current
      ? Axios.patch(`${BACKEND_URL}package/${id}/deactivate`)
      : Axios.patch(`${BACKEND_URL}package/${id}/activate`);
    req
      .then((res) => {
        toast.success(res?.data?.message || "Updated successfully");
        fetchDeals(page, search, sortOrder);
      })
      .catch(showError)
      .finally(() => setLoading(false));
  };

  const columns = [
    {
      name: "image",
      label: "Image",
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (i) => (
          <img src={dealsWithExpiry[i]?.image} alt="" style={{ width: 60, height: 40, objectFit: "contain", backgroundColor: "#f0f0f0", borderRadius: 4 }} />
        ),
      },
    },
    {
      name: "hotel_name",
      label: "Hotel Name",
      options: {
        customBodyRenderLite: (i) => dealsWithExpiry[i]?.hotel_name || dealsWithExpiry[i]?.hotel_id || "-",
      },
    },
    { name: "title", label: "Title" },
    {
      name: "start_date",
      label: "Dates",
      options: {
        customBodyRenderLite: (i) =>
          `${formatDisplayDate(dealsWithExpiry[i]?.start_date)} - ${formatDisplayDate(dealsWithExpiry[i]?.end_date)}`,
      },
    },
    {
      name: "number_of_nights",
      label: "Nights",
      options: {
        customBodyRenderLite: (i) =>
          calculateNights(dealsWithExpiry[i]?.start_date, dealsWithExpiry[i]?.end_date) || dealsWithExpiry[i]?.number_of_nights || "-",
      },
    },
    {
      name: "influencer_name",
      label: "Creator Name",
      options: {
        customBodyRenderLite: (i) => dealsWithExpiry[i]?.influencer_name || "-",
      },
    },
    {
      name: "daysUntilExpiry",
      label: "Expiration",
      options: {
        filter: false,
        customBodyRender: (value) => {
          const status = getExpiryStatus(value);
          return <Chip label={status.label} size="small" color={status.color} />;
        },
      },
    },
    {
      name: "is_active",
      label: "Status",
      options: {
        customBodyRenderLite: (i) => (dealsWithExpiry[i]?.is_active ? "Active" : "Inactive"),
      },
    },
    write && {
      name: "actions",
      label: "Actions",
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (i) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Switch
              checked={Boolean(dealsWithExpiry[i]?.is_active)}
              disabled={loading}
              onChange={() => toggleStatus(dealsWithExpiry[i]?._id, dealsWithExpiry[i]?.is_active)}
              size="small"
            />
            <Edit sx={{ color: "#1976d2", fontSize: 20, cursor: "pointer" }} onClick={() => navigate(`/deals/edit/${dealsWithExpiry[i]?._id}`)} />
            <ContentCopy
              sx={{ color: "#616161", fontSize: 18, cursor: "pointer" }}
              onClick={() => cloneDeal(dealsWithExpiry[i]?._id)}
            />
            <DeleteOutline
              sx={{ color: "red", fontSize: 20, cursor: "pointer" }}
              onClick={() => setDeleteState({ status: true, id: dealsWithExpiry[i]?._id })}
            />
          </Box>
        ),
      },
    },
  ].filter(Boolean);

  return (
    <div className="container-fluid">
      <Helmet>
        <title>Deals</title>
      </Helmet>
      <div className="d-flex justify-content-between align-center mb-3">
        <h2>Deals</h2>
        {write && (
          <Button variant="contained" onClick={() => navigate("/deals/add")}>
            Add Deal
          </Button>
        )}
      </div>
      <MUIDataTable
        title="Deals list"
        data={dealsWithExpiry}
        columns={columns}
        options={{
          responsive: "standard",
          selectableRows: "none",
          filter: false,
          search: true,
          viewColumns: false,
          serverSide: true,
          count,
          page,
          sortOrder,
          rowsPerPageOptions: [],
          download: false,
          onTableChange: (action, tableState) => {
            if (action === "changePage") {
              setPage(tableState.page);
              fetchDeals(tableState.page, search, sortOrder);
            } else if (action === "sort") {
              const newSortOrder = tableState.sortOrder;
              setSortOrder(newSortOrder);
              setPage(0);
              fetchDeals(0, search, newSortOrder);
            }
          },
          onSearchChange: (searchText) => {
            const searchValue = searchText || "";
            setSearch(searchValue);
            setPage(0);
            fetchDeals(0, searchValue, sortOrder);
          },
        }}
      />

      <Dialog open={deleteState.status} onClose={() => setDeleteState({ status: false, id: "" })} fullWidth>
        <div className="d-flex justify-content-between align-center p-3">
          <h3>Delete deal</h3>
          <IconButton onClick={() => setDeleteState({ status: false, id: "" })}>
            <Close />
          </IconButton>
        </div>
        <DialogContent dividers>Are you sure you want to delete this deal?</DialogContent>
        <DialogActions>
          <Button className="cancel-button" onClick={() => setDeleteState({ status: false, id: "" })}>
            Cancel
          </Button>
          <Button className="submit-button" variant="contained" disabled={loading} onClick={deleteDeal}>
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Deals;
